import { translate } from 'shared/lib/i18n.tsx';
import { defaultRemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import { getRemoteCalibStandZoneMetrics } from 'shared/lib/tv-game/remoteCalibStandZone.ts';

import type { TvQualityProfile } from '../infrastructure/qualityProfile.ts';
import { drawRemoteCalibrationStandZone } from '../infrastructure/canvas/room/calibrationShared.ts';
import { wrapCanvasText } from '../infrastructure/canvas/room/canvasHelpers.ts';
import {
  createBirdFlightGame,
  type BirdFlightGame,
  type BirdFlightMap,
  BIRD_FLIGHT_DEFAULT_MAP,
  BIRD_FLIGHT_MAPS,
  type BirdFlightPose,
} from '../games/bird-flight/birdFlightGame.ts';

/**
 * «Полёт птицы» на ТВ (GEN-18150). Игровой мир живёт здесь, телефон —
 * контроллер: считает позу и шлёт кадр
 * `{type: 'BirdFlightData', gameState, pose}` ~30 раз/с. ТВ сам ведёт звёзды и
 * таймер, по окончании отдаёт счёт через `onFinished` → `game:state gameover`.
 *
 * Своя WebGL-канва поверх общей 2D (`#canvas`) и под лобби. Создаётся на
 * старте и уничтожается в `reset()`: цикл Three.js не должен крутиться, пока
 * идёт другая игра.
 */
export type BirdFlightPhase = 'lobby' | 'calibration' | 'playing' | 'paused' | 'gameover';

type BirdFlightGameState = 'PREPARING' | 'STARTED' | 'PAUSED' | 'GAME_OVER';

/** Модели птиц лежат в `public/media/bird-flight/`; `pitch` — как в приложениях. */
const BIRDS: Record<string, { url: string; pitch: number }> = {
  blue: { url: '/media/bird-flight/blue.glb', pitch: 1.3 },
  berkut: { url: '/media/bird-flight/berkut.glb', pitch: 0.75 },
};

const Z_INDEX = '1';

const MAP_TITLE_KEYS = {
  ekibastuz: 'map.ekibastuz',
  astana: 'map.astana',
  almaty: 'map.almaty',
  barcelona: 'map.barcelona',
  paris: 'map.paris',
} as const satisfies Record<BirdFlightMap, string>;

export class BirdFlightDisplayEngine {
  private phase: BirdFlightPhase = 'lobby';

  private host: HTMLDivElement | null = null;

  private statusEl: HTMLDivElement | null = null;

  private calibCanvas: HTMLCanvasElement | null = null;

  /** Экран «строим мир» на весь экран, пока мир не готов. */
  private loadingEl: HTMLDivElement | null = null;

  private loadingBar: HTMLDivElement | null = null;

  private loadingText: HTMLDivElement | null = null;

  private loadProgress = 0;

  private worldReady = false;

  private calibRaf = 0;

  /** Отложенная сборка мира: калибровка рисуется сразу, тяжёлый мир — после первого кадра. */
  private buildRaf = 0;

  private buildTimer = 0;

  /** Заполнение рамки на телефоне, 0…1 (`detectionProgress` кадра подготовки). */
  private detectionProgress = 0;

  private game: BirdFlightGame | null = null;

  /** Карта и птица с телефона (поля `map`/`bird` кадра). До первого кадра — карта по умолчанию и птица из примитивов. */
  private map: BirdFlightMap = BIRD_FLIGHT_DEFAULT_MAP;

  private bird: string | null = null;

  onFinished?: (stars: number) => void;

  /**
   * Постройка карты — телефону: он ждёт с калибровкой до `onWorldReady`, как
   * без ТВ. `map` — какая карта: до первого кадра с телефона ТВ может начать
   * строить каньон, а потом пересобрать мир под выбранную карту.
   */
  onWorldLoading?: (progress: number, map: BirdFlightMap) => void;

  onWorldReady?: (map: BirdFlightMap) => void;

  /** `ready` для текущего мира уже отправлен телефону. */
  private readyAnnounced = false;

  /** Последний отправленный процент — шлём не каждый шаг постройки. */
  private sentLoadPct = -1;

  constructor(private readonly quality: TvQualityProfile) {}

  getPhase(): BirdFlightPhase {
    return this.phase;
  }

  /** Игрок встаёт перед камерой телефона; мир на фоне стоит. */
  startCalibration(): void {
    // Сначала на экран — «встаньте в зону»: сборка мира (Экибастуз — сотни
    // мешей) блокирует поток на секунды, и экран калибровки иначе появлялся
    // бы с задержкой и подвисал. Мир строим следом, пока игрок встаёт.
    this.ensureHost();
    this.game?.reset(); // новый забег после рестарта: мир от прошлого уже есть
    this.detectionProgress = 0;
    this.phase = 'calibration';
    this.showStatus(null);
    this.showCalibration(true);
    this.updateLoading();
    this.readyAnnounced = false;
    this.announceReady();
    this.scheduleGameBuild();
  }

  /** `start-game` с телефона: калибровка на телефоне пройдена. */
  startGame(): void {
    this.cancelGameBuild();
    this.ensureGame();
    this.game?.start();
    this.phase = 'playing';
    this.showCalibration(false);
    this.showStatus(null);
    this.updateLoading();
  }

  onGameFrame(rawFrame: unknown): void {
    if (!rawFrame || typeof rawFrame !== 'object') return;
    if (this.handlePreparingFrame(rawFrame as Record<string, unknown>)) return;
    if (!this.game) {
      // Кадр PREPARING приходит раньше, чем ТВ создаёт игру: запоминаем выбор.
      const raw = rawFrame as Record<string, unknown>;
      this.applySetup(raw.map, raw.bird);
      return;
    }
    const raw = rawFrame as Record<string, unknown>;
    const state = raw.gameState as BirdFlightGameState | undefined;
    this.applySetup(raw.map, raw.bird);

    if (state === 'PAUSED' && this.phase === 'playing') {
      this.game.pause();
      this.phase = 'paused';
      this.showStatus(translate('game.pause'));
      return;
    }
    if (state === 'STARTED' && this.phase === 'paused') {
      this.game.resume();
      this.phase = 'playing';
      this.showStatus(null);
    }
    // Телефон закончил забег («Закончить» в меню): итог уйдёт через `onFinished`.
    if (state === 'GAME_OVER' && (this.phase === 'playing' || this.phase === 'paused')) {
      this.game.finish();
      return;
    }

    const pose = parsePose(raw.pose);
    if (pose) this.game.setPose(pose);
  }

  /**
   * Кадр `PREPARING` — телефон в калибровке. Во время неё он несёт прогресс
   * заполнения рамки; после «Начать заново» (игра идёт, стоит на паузе или
   * закончена) — ещё и просьба снова встать в рамку. Возвращает `true`, если
   * кадр разобран целиком.
   */
  private handlePreparingFrame(raw: Record<string, unknown>): boolean {
    if (raw.gameState !== 'PREPARING') return false;
    this.applySetup(raw.map, raw.bird);
    if (this.phase === 'lobby') return true;
    if (this.phase !== 'calibration') {
      this.startCalibration(); // рестарт с телефона
    }
    this.announceReady(); // телефон зашёл, а мир этой карты уже готов
    const progress = raw.detectionProgress;
    if (typeof progress === 'number' && Number.isFinite(progress)) {
      this.detectionProgress = Math.min(1, Math.max(0, progress));
    }
    return true;
  }

  /**
   * Телефон выбрал другую карту или птицу — пересоздаём мир. Фаза остаётся:
   * если забег уже идёт, новый стартует сразу.
   */
  private applySetup(rawMap: unknown, rawBird: unknown): void {
    const maps: readonly unknown[] = BIRD_FLIGHT_MAPS;
    const map: BirdFlightMap = maps.includes(rawMap) ? rawMap as BirdFlightMap : this.map;
    const bird = typeof rawBird === 'string' && BIRDS[rawBird] ? rawBird : this.bird;
    if (map === this.map && bird === this.bird) return;
    this.map = map;
    this.bird = bird;
    if (!this.game) return;
    const wasPlaying = this.phase === 'playing';
    this.game.destroy();
    this.game = null;
    this.host?.remove();
    this.host = null;
    this.statusEl = null;
    this.calibCanvas = null;
    this.loadingEl = null;
    this.ensureGame();
    if (wasPlaying) this.game?.start();
    else this.showCalibration(this.phase === 'calibration');
  }

  reset(): void {
    this.phase = 'lobby';
    this.cancelGameBuild();
    this.showCalibration(false);
    this.game?.destroy();
    this.game = null;
    this.host?.remove();
    this.host = null;
    this.statusEl = null;
    this.calibCanvas = null;
    this.loadingEl = null;
  }

  /** Контейнер, статус и канва калибровки — дёшево, без мира. */
  private ensureHost(): HTMLDivElement {
    if (this.host) return this.host;
    const host = document.createElement('div');
    host.style.cssText = `position:fixed;inset:0;z-index:${Z_INDEX};`;
    document.body.appendChild(host);

    const status = document.createElement('div');
    status.style.cssText = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:2;'
      + 'padding:24px 40px;border-radius:24px;background:rgba(20,14,50,.75);color:#fff;'
      + 'font:800 40px -apple-system,system-ui,sans-serif;text-align:center;display:none;';
    host.appendChild(status);

    const calib = document.createElement('canvas');
    calib.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:1;display:none;';
    host.appendChild(calib);

    const loading = document.createElement('div');
    loading.style.cssText = 'position:absolute;z-index:3;display:none;flex-direction:column;align-items:center;'
      + 'justify-content:center;gap:18px;color:#fff;font:800 34px -apple-system,system-ui,sans-serif;';
    const text = document.createElement('div');
    const track = document.createElement('div');
    track.style.cssText = 'width:min(60vw,720px);height:14px;border-radius:7px;'
      + 'background:rgba(255,255,255,.18);overflow:hidden;';
    const bar = document.createElement('div');
    bar.style.cssText = 'height:100%;width:0;border-radius:7px;background:linear-gradient(90deg,#5be37d,#ffe566);'
      + 'transition:width .25s ease-out;';
    track.appendChild(bar);
    loading.append(text, track);
    host.appendChild(loading);

    this.host = host;
    this.statusEl = status;
    this.calibCanvas = calib;
    this.loadingEl = loading;
    this.loadingBar = bar;
    this.loadingText = text;
    return host;
  }

  /** Мир строится после первой отрисовки калибровки; успеет и до `start-game`, и после. */
  private scheduleGameBuild(): void {
    if (this.game || this.buildRaf || this.buildTimer) return;
    this.buildRaf = requestAnimationFrame(() => {
      this.buildRaf = 0;
      this.buildTimer = window.setTimeout(() => {
        this.buildTimer = 0;
        if (this.game || this.phase === 'lobby') return;
        this.ensureGame();
      }, 0);
    });
  }

  private cancelGameBuild(): void {
    cancelAnimationFrame(this.buildRaf);
    window.clearTimeout(this.buildTimer);
    this.buildRaf = 0;
    this.buildTimer = 0;
  }

  private ensureGame(): void {
    if (this.game) return;
    const host = this.ensureHost();

    // Слабым ТВ — рендер в родном размере без сглаживания: WebGL на
    // webOS/Tizen дорогой, а позу считает телефон, не ТВ.
    const isLight = this.quality !== 'high';
    this.loadProgress = 0;
    this.worldReady = false;
    this.readyAnnounced = false;
    this.sentLoadPct = -1;
    this.updateLoading();
    this.game = createBirdFlightGame({
      host,
      pixelRatio: isLight ? 1 : Math.min(devicePixelRatio, 1.5),
      antialias: !isLight,
      map: this.map,
      metersTemplate: translate('game.meters'),
      ...(this.bird && BIRDS[this.bird] ? { birdModel: BIRDS[this.bird] } : {}),
      onLoadProgress: (progress) => {
        this.loadProgress = progress;
        this.updateLoading();
        // Каждые 5 %: шагов постройки ~20, канал телефону столько не нужно.
        const pct = Math.floor(progress * 20);
        if (pct !== this.sentLoadPct) {
          this.sentLoadPct = pct;
          this.onWorldLoading?.(progress, this.map);
        }
      },
      onWorldReady: () => {
        this.worldReady = true;
        this.updateLoading();
        this.announceReady();
      },
      onFinished: ({ stars }) => {
        this.phase = 'gameover';
        this.showStatus(translate('game.flight_over', { stars }));
        this.onFinished?.(stars);
      },
    });
  }

  /**
   * Экран «встаньте в зону», как у Target Attack, но с силуэтом по пояс (`upper`):
   * птице нужны только плечи и запястья. Перерисовываем каждый кадр, пока идёт
   * калибровка — силуэт грузится асинхронно, а окно может менять размер.
   */
  private showCalibration(visible: boolean): void {
    cancelAnimationFrame(this.calibRaf);
    this.calibRaf = 0;
    if (!this.calibCanvas) return;
    this.calibCanvas.style.display = visible ? 'block' : 'none';
    if (!visible) return;
    const tick = (): void => {
      if (this.phase !== 'calibration') return;
      this.drawCalibration();
      this.calibRaf = requestAnimationFrame(tick);
    };
    tick();
  }

  private drawCalibration(): void {
    const canvas = this.calibCanvas;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;
    ctx.clearRect(0, 0, W, H);

    const layout = defaultRemoteViewLayout(W, H);
    // Подложка — на весь экран и непрозрачная: игровой прямоугольник `layout`
    // уже экрана ТВ (по бокам оставались полосы), а мир под калибровкой не нужен.
    const backdrop = ctx.createLinearGradient(0, 0, 0, H);
    backdrop.addColorStop(0, '#040c26');
    backdrop.addColorStop(0.6, '#07143a');
    backdrop.addColorStop(1, '#04081c');
    ctx.fillStyle = backdrop;
    ctx.fillRect(0, 0, W, H);
    drawRemoteCalibrationStandZone(ctx, layout, 'upper');

    const {
      cx, boxX, boxY, boxW, boxH, borderW,
    } = getRemoteCalibStandZoneMetrics(layout, 'upper');
    drawFillProgress(ctx, {
      x: boxX, y: boxY, w: boxW, h: boxH, lineWidth: borderW * 1.6, progress: this.detectionProgress,
    });
    const px = Math.round(Math.min(layout.gameHeight * 0.034, boxW / 14, 46));
    const padX = boxW * 0.05;
    const sw = boxW - padX * 2;
    const lineGap = px * 1.45;
    ctx.save();
    ctx.font = `600 ${px}px Arial, sans-serif`;
    const lines = wrapCanvasText(ctx, translate('calib.hint.upper_body'), sw);
    const padV = px * 0.55;
    const sy = boxY + borderW * 2.2 + Math.max(10, layout.gameHeight * 0.015);
    ctx.fillStyle = 'rgba(0,0,0,0.58)';
    ctx.beginPath();
    ctx.rect(boxX + padX, sy, sw, lines.length * lineGap + padV * 2);
    ctx.fill();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffe566';
    lines.forEach((line, i) => ctx.fillText(line, cx, sy + padV + lineGap * (i + 0.5)));
    ctx.restore();
  }

  /** Сообщить телефону, что мир готов, — один раз на мир и на заход в калибровку. */
  private announceReady(): void {
    if (!this.worldReady || this.readyAnnounced || this.phase === 'lobby') return;
    this.readyAnnounced = true;
    this.onWorldReady?.(this.map);
  }

  /**
   * Плашка постройки мира — на весь экран, поверх калибровки: как на
   * телефоне, сначала карта, потом калибровка (телефон до `ready` не калибрует).
   */
  private updateLoading(): void {
    const el = this.loadingEl;
    if (!el || !this.loadingBar || !this.loadingText) return;
    const visible = !this.worldReady && this.phase !== 'lobby' && this.phase !== 'gameover';
    el.style.display = visible ? 'flex' : 'none';
    if (!visible) return;
    el.style.cssText += 'inset:0;bottom:auto;left:0;transform:none;height:100%;width:100%;padding:0;border-radius:0;'
      + 'background:radial-gradient(circle at 50% 40%,#1b2a6b,#07143a 70%);';
    this.loadingText.textContent = translate('game.building_world', {
      map: translate(MAP_TITLE_KEYS[this.map]),
      pct: Math.round(this.loadProgress * 100),
    });
    this.loadingBar.style.width = `${Math.round(this.loadProgress * 100)}%`;
  }

  private showStatus(text: string | null): void {
    if (!this.statusEl) return;
    this.statusEl.textContent = text ?? '';
    this.statusEl.style.display = text ? 'block' : 'none';
  }
}

function parsePoint(value: unknown): { x: number; y: number } | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as Record<string, unknown>;
  return typeof x === 'number' && typeof y === 'number' ? { x, y } : null;
}

function parsePose(value: unknown): BirdFlightPose | null {
  if (!value || typeof value !== 'object') return null;
  const p = value as Record<string, unknown>;
  const leftShoulder = parsePoint(p.leftShoulder);
  const rightShoulder = parsePoint(p.rightShoulder);
  const leftWrist = parsePoint(p.leftWrist);
  const rightWrist = parsePoint(p.rightWrist);
  if (!leftShoulder || !rightShoulder || !leftWrist || !rightWrist) return null;
  return {
    leftShoulder, rightShoulder, leftWrist, rightWrist,
  };
}

/**
 * Заполнение рамки, как на телефоне: зелёная линия идёт по часовой стрелке от
 * верхней середины; при полном заполнении рамка целиком зелёная с подсветкой.
 */
export function drawFillProgress(
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; w: number; h: number; lineWidth: number; progress: number },
): void {
  const {
    x, y, w, h, lineWidth, progress,
  } = rect;
  if (progress <= 0) return;
  const r = Math.min(w, h) * 0.05;
  const perimeter = 2 * (w + h) - 8 * r + 2 * Math.PI * r;
  const right = x + w;
  const bottom = y + h;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x + w / 2, y);
  ctx.lineTo(right - r, y);
  ctx.arc(right - r, y + r, r, -Math.PI / 2, 0);
  ctx.lineTo(right, bottom - r);
  ctx.arc(right - r, bottom - r, r, 0, Math.PI / 2);
  ctx.lineTo(x + r, bottom);
  ctx.arc(x + r, bottom - r, r, Math.PI / 2, Math.PI);
  ctx.lineTo(x, y + r);
  ctx.arc(x + r, y + r, r, Math.PI, Math.PI * 1.5);
  ctx.lineTo(x + w / 2, y);
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.strokeStyle = '#4cd964';
  if (progress >= 1) {
    ctx.shadowColor = 'rgba(76, 217, 100, 0.9)';
    ctx.shadowBlur = lineWidth * 4;
  } else {
    ctx.setLineDash([perimeter * progress, perimeter]);
  }
  ctx.stroke();
  ctx.restore();
}
